import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MapPin, Clock, HelpCircle, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20he%20leido%20tu%20articulo%20sobre%20stock%20y%20quiero%20revisar%20mi%20caso";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const FAQS = [
  {
    q: "¿Cómo sé si el problema del stock está afectando a la rentabilidad de mi empresa?",
    a: "Si tu inventario teórico no cuadra con el real, si tienes referencias paradas durante meses o si haces compras de urgencia con frecuencia, ya hay un problema de rentabilidad. Los síntomas no se ven en facturación, se ven en margen y caja."
  },
  {
    q: "¿Cuál es la diferencia entre stock teórico y stock real, y por qué importa?",
    a: "El stock teórico es lo que dice tu sistema (ERP, CRM, hojas de cálculo); el stock real es lo que hay en el almacén. Cuando no coinciden, todas las decisiones que tomes —compras, precios, presupuesto— estarán basadas en información incorrecta."
  },
  {
    q: "¿Por qué un exceso de inventario es un problema de rentabilidad?",
    a: "Cada producto en almacén es dinero inmovilizado: ocupa espacio, paga impuestos, se devalúa y deja de generar liquidez. Mucho stock no significa estar preparado, suele significar mala planificación y rentabilidad bloqueada."
  },
  {
    q: "¿Qué papel juega la limpieza de datos en la gestión de stock?",
    a: "Es el paso previo a cualquier análisis útil. Duplicados, referencias mal codificadas y movimientos sin registrar hacen que el dato sea engañoso. Sin datos limpios, los dashboards mienten y las decisiones cuestan dinero."
  },
  {
    q: "¿Por dónde se empieza para mejorar la gestión de stock con análisis de datos?",
    a: "Primero limpiar y unificar las fuentes (ERP, almacén, ventas). Después definir KPIs claros: rotación, días de cobertura, stock muerto y margen real por referencia. Y por último un dashboard que cualquiera del equipo pueda leer en 30 segundos."
  }
];

export default function BlogArticle4() {
  useEffect(() => {
    document.title = "El problema del stock que afecta a la rentabilidad de tu empresa | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "El stock mal gestionado es uno de los mayores problemas de rentabilidad que las empresas no detectan. Inventario, datos sucios y dinero inmovilizado: por qué ocurre y cómo solucionarlo con análisis de datos."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/blog/problema-stock-rentabilidad-empresas");
    }

    // FAQPage JSON-LD
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "stock-faq-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("stock-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="blog-article4-page">
      <div className="noise-overlay" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="article4-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4">
              <img src={LOGO_URL} alt="DATYON" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-indigo-400 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <article className="relative section-padding pt-32 overflow-hidden" data-testid="article4-content">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-8">
                <Link to="/blog" className="inline-flex items-center text-slate-400 hover:text-indigo-400 text-sm transition-colors mb-6">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al blog
                </Link>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-3 py-1 rounded-full">Stock e inventario</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> 6 min de lectura</span>
                </div>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-8">
                El problema del stock que está afectando a la rentabilidad de muchas empresas (y casi nadie ve)
              </motion.h1>

              <motion.div variants={fadeInUp} className="prose-custom text-slate-400 text-base leading-relaxed space-y-5">
                <p className="text-lg text-slate-300">Hay empresas que venden.</p>

                <p>Que facturan.</p>

                <p>Que tienen movimiento.</p>

                <p>
                  Pero tienen un problema silencioso que impacta directamente en su dinero: el stock.
                </p>

                <p>No hablamos solo de almacén.</p>

                <p>
                  Hablamos de dinero inmovilizado, errores en inventario y decisiones basadas en datos incorrectos.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">El stock no es solo producto… es dinero</h2>

                <p>Cada producto que tienes en almacén es dinero parado.</p>

                <p>Si no rota, no genera ingresos.</p>

                <p>
                  Y si no está bien controlado, puede distorsionar completamente la realidad de tu negocio. Una mala gestión de stock empresa convierte un balance aparentemente sano en una rentabilidad mucho peor de la que parece sobre el papel.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">El problema más común: el inventario no cuadra</h2>

                <p>Esto pasa más de lo que parece.</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Stock teórico ≠ stock real</li>
                  <li>Productos mal registrados</li>
                  <li>Movimientos no controlados</li>
                </ul>

                <p>Y esto tiene consecuencias directas:</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Decisiones equivocadas</li>
                  <li>Compras innecesarias</li>
                  <li>Pérdidas invisibles</li>
                </ul>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">El impacto en Hacienda (y aquí duele)</h2>

                <p>El valor del inventario afecta directamente a:</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Resultados de la empresa</li>
                  <li>Impuestos</li>
                  <li>Contabilidad</li>
                </ul>

                <p>
                  Si el stock no está bien calculado, puedes estar declarando mal o tomando decisiones con datos incorrectos. En los dos escenarios, hay dinero saliendo de tu empresa que no debería salir.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Dinero muerto que nadie está viendo</h2>

                <p>Mucho stock no es buena señal.</p>

                <p>Puede significar:</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Producto que no rota</li>
                  <li>Mala planificación</li>
                  <li>Exceso de compra</li>
                </ul>

                <p>
                  En todos los casos, hablamos de dinero parado que podría estar generando rentabilidad.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">El problema real: los datos están sucios</h2>

                <p>En la mayoría de casos, el problema no es el stock.</p>

                <p>Es la calidad de los datos.</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Duplicados</li>
                  <li>Errores</li>
                  <li>Falta de estructura</li>
                  <li>Sistemas desconectados</li>
                </ul>

                <p>Y así es imposible analizar bien.</p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Sin datos limpios, no hay decisiones buenas</h2>

                <p>Antes de analizar, hay que limpiar.</p>

                <p>
                  Porque si los datos están mal, el análisis también lo estará.
                </p>

                <p>Aquí es donde muchas empresas fallan.</p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">¿Cuánto te está afectando esto?</h2>

                <p>Depende de tu empresa.</p>

                <p>
                  Pero en muchos casos, el impacto en la rentabilidad de la empresa es mayor de lo que parece a simple vista.
                </p>

                {/* Mid-article CTA to ROI Calculator */}
                <div className="not-prose my-8 p-6 rounded-2xl bg-gradient-to-br from-indigo-600/15 to-slate-900/60 border border-indigo-500/30" data-testid="article4-mid-cta">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-indigo-600/30 flex items-center justify-center text-indigo-300 flex-shrink-0">
                      <Calculator className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1 text-lg">Calcula cuánto te está costando</h3>
                      <p className="text-slate-400 text-sm mb-3">
                        En menos de un minuto puedes hacer una estimación honesta de cuánto dinero podrías estar perdiendo por falta de visibilidad sobre tu inventario y tus datos.
                      </p>
                      <Link
                        to="/calculadora-roi-power-bi-murcia"
                        className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
                        data-testid="article4-mid-cta-link"
                      >
                        Abrir la calculadora ROI Power BI
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Cómo solucionarlo</h2>

                <p>No se trata solo de controlar el stock.</p>

                <p>Se trata de:</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Tener datos fiables</li>
                  <li>Entender qué está pasando</li>
                  <li>Tomar decisiones correctas</li>
                </ul>

                <p>
                  Aquí es donde entra el{" "}
                  <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    análisis de datos para empresas
                  </Link>
                  : limpiar las fuentes, conectar los sistemas y construir un panel donde de verdad puedas leer el estado de tu inventario y de tu empresa.
                </p>

                <p>
                  Y si quieres entender qué métricas deberías controlar para asegurar la rentabilidad del negocio, este artículo te puede interesar:{" "}
                  <Link to="/blog/que-kpis-medir-empresa-rentable" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    qué KPIs debe medir una empresa para ser rentable
                  </Link>
                  .
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Conclusión</h2>

                <p>El stock mal gestionado no es un problema operativo.</p>

                <p>Es un problema de rentabilidad.</p>

                <p>
                  Y muchas empresas no lo detectan hasta que ya es tarde.
                </p>
              </motion.div>

              {/* FAQ */}
              <motion.div variants={fadeInUp} className="mt-16">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article4-faq-h2">
                  Preguntas frecuentes sobre <span className="text-gradient">stock e inventario</span>
                </h2>
                <div className="space-y-4">
                  {FAQS.map((faq, i) => (
                    <Card key={i} className="bg-slate-900 border-slate-800" data-testid={`article4-faq-${i}`}>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                          {faq.q}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed pl-8">{faq.a}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>

              {/* Final CTA */}
              <motion.div variants={fadeInUp} className="mt-16 glass-card rounded-2xl p-8 md:p-10 text-center" data-testid="article4-cta">
                <h2 className="text-2xl font-bold text-white mb-3">
                  ¿Quieres saber cuánto dinero hay <span className="text-gradient">parado en tu stock</span>?
                </h2>
                <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                  En 30 minutos puedo analizar tu caso y ayudarte a detectar dónde están los errores en tus datos de stock y cómo están afectando a tu negocio. Sin compromiso, sin coste.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 rounded-full" data-testid="article4-cta-contact">
                    <Link to="/contacto">
                      Solicita tu diagnóstico gratuito
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-6 rounded-full border border-slate-700" data-testid="article4-cta-roi">
                    <Link to="/calculadora-roi-power-bi-murcia">
                      Calcular mi ROI primero
                    </Link>
                  </Button>
                </div>
              </motion.div>

              {/* Related links */}
              <motion.div variants={fadeInUp} className="mt-12 text-sm text-slate-500">
                <p>
                  Artículos relacionados:{" "}
                  <Link to="/blog/que-kpis-medir-empresa-rentable" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Qué KPIs debe medir una empresa para ser rentable
                  </Link>{" · "}
                  <Link to="/blog/como-saber-si-tu-empresa-pierde-dinero" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Cómo saber si tu empresa está perdiendo dinero
                  </Link>
                </p>
                <p className="mt-2">
                  Servicios:{" "}
                  <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Análisis de datos para empresas
                  </Link>{" · "}
                  <Link to="/calculadora-roi-power-bi-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Calculadora ROI Power BI Murcia
                  </Link>
                </p>
              </motion.div>
            </motion.div>
          </div>
        </article>
      </main>

      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
      </a>

      <CookieBanner />
    </div>
  );
}
